import { useState, useEffect } from "react";
import {
  FiCalendar,
  FiClock,
  FiHome,
  FiActivity,
  FiDroplet,
  FiTrendingUp,
  FiBell,
  FiSun,
  FiAlertTriangle,
  FiAlertCircle,
} from "react-icons/fi";
import { IoMdCalculator } from "react-icons/io";
import { Link } from "react-router-dom";
import {
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameDay,
  format,
  addMonths,
  subMonths,
  addDays,
} from "date-fns";
import { Helmet } from "react-helmet-async";


const PeriodTracker = () => {
  const [lastPeriodDate, setLastPeriodDate] = useState("");
  const [cycleLength, setCycleLength] = useState(28);
  const [periodLength, setPeriodLength] = useState(5);
  const [nextPeriod, setNextPeriod] = useState(null);
  const [fertileWindow, setFertileWindow] = useState([]);
  const [currentDate] = useState(new Date());
  const [showResults, setShowResults] = useState(false);
  const [periodDays, setPeriodDays] = useState([]);


  useEffect(() => {
    if (showResults && lastPeriodDate) {
      calculateCycle();
    }
  }, [lastPeriodDate, cycleLength, periodLength, showResults]);


  const calculateCycle = () => {
    const startDate = new Date(lastPeriodDate);
    const cyclesToCalculate = 3; // previous, current, next


    let allPeriodDays = [];
    let allFertileDays = [];


    // Start one cycle earlier
    const adjustedStartDate = addDays(startDate, -cycleLength);


    for (let i = 0; i < cyclesToCalculate; i++) {
      const cycleStart = addDays(adjustedStartDate, i * cycleLength);


      // Period days
      const period = Array.from({ length: periodLength }, (_, j) => {
        return addDays(cycleStart, j);
      });
      allPeriodDays.push(...period);


      // Ovulation and fertile window
      const ovulationDay = addDays(cycleStart, cycleLength - 14);
      const fertileStart = addDays(ovulationDay, -3);
      const fertileEnd = addDays(ovulationDay, 1);


      const fertile = eachDayOfInterval({
        start: fertileStart,
        end: fertileEnd,
      });
      allFertileDays.push(...fertile);
    }


    // Keep next period unchanged
    const next = addDays(startDate, cycleLength);
    setNextPeriod(next);
    setPeriodDays(allPeriodDays);
    setFertileWindow(allFertileDays);
  };


  const handleSubmit = (e) => {
    e.preventDefault();
    if (lastPeriodDate) {
      setShowResults(true);
      calculateCycle();
    }
  };


  const formatDate = (date) => {
    if (!date) return "--";
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };


  const getCycleDay = () => {
    if (!lastPeriodDate) return "--";
    const lastDate = new Date(lastPeriodDate);
    const diffTime = Math.abs(currentDate - lastDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > cycleLength ? "--" : diffDays;
  };


  const getCycleProgress = () => {
    const day = getCycleDay();
    return day === "--" ? 0 : (day / cycleLength) * 100;
  };


  const generateMonthCalendar = (monthDate) => {
    const start = startOfMonth(monthDate);
    const end = endOfMonth(monthDate);
    const days = eachDayOfInterval({ start, end });


    return (
      <div
        key={monthDate}
        className="text-sm bg-white rounded-2xl p-4 shadow-md"
      >
        <h4 className="text-lg font-semibold text-center mb-2">
          {format(monthDate, "MMMM yyyy")}
        </h4>
        <div className="grid grid-cols-7 text-center font-medium text-gray-600 mb-1">
          {["S", "M", "T", "W", "T", "F", "S"].map((d) => (
            <div key={d}>{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1 text-center">
          {Array(start.getDay())
            .fill("")
            .map((_, i) => (
              <div key={`empty-${i}`} />
            ))}
          {days.map((day) => {
            const isPeriod = periodDays.some((d) => isSameDay(d, day));
            const isFertile = fertileWindow.some((d) => isSameDay(d, day));


            let bgColor = "";
            if (isPeriod) bgColor = "bg-pink-400 text-white";
            else if (isFertile) bgColor = "bg-green-200";


            return (
              <div key={day} className={`p-2 rounded-full ${bgColor}`}>
                {day.getDate()}
              </div>
            );
          })}
        </div>
      </div>
    );
  };


  // Dynamically base calendar on selected period date
  let baseDate = currentDate;
  if (lastPeriodDate) baseDate = new Date(lastPeriodDate);


  const prevMonth = subMonths(baseDate, 1);
  const nextMonth = addMonths(baseDate, 1);


  return (
    <div className="w-full font-nunito min-h-screen bg-gradient-to-br from-pink-50 to-purple-50">
    <Helmet>
    {/* Google Analytics */}
    <script
      async
      src="https://www.googletagmanager.com/gtag/js?id=G-YR8YMW9ERY"
    ></script>
    <script>
      {`
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', 'G-YR8YMW9ERY', {
          page_path: window.location.pathname,
        });
      `}
    </script>
    <title>Period Tracker - FlowHearts | Menstrual Cycle & Ovulation Tracking</title>
    <meta
      name="description"
      content="Track your menstrual cycle, ovulation, and fertile days with FlowHearts' Period Tracker. Monitor hormone balance, predict periods, and boost women's health."
    />

    {/* Canonical URL */}
    <link rel="canonical" href="https://flowhearts.com/period-tracker" />

    {/* Open Graph / Facebook */}
    <meta property="og:type" content="website" />
    <meta property="og:url" content="https://flowhearts.com/period-tracker" />
    <meta
      property="og:title"
      content="Period Tracker - FlowHearts | Menstrual Cycle & Ovulation Tracking"
    />
    <meta
      property="og:description"
      content="Monitor your menstrual cycle, ovulation, and fertility with FlowHearts' Period Tracker. Predict periods and understand hormonal changes for better women's health."
    />
    <meta
      property="og:image"
      content="https://flowhearts.com/images/homeimg.jpg"
    />

    {/* Twitter */}
    <meta property="twitter:card" content="summary_large_image" />
    <meta property="twitter:url" content="https://flowhearts.com/period-tracker" />
    <meta
      property="twitter:title"
      content="Period Tracker - FlowHearts | Menstrual Cycle & Ovulation Tracking"
    />
    <meta
      property="twitter:description"
      content="Use FlowHearts' Period Tracker to track menstrual cycles, ovulation, and fertility. Stay informed about hormonal health and period predictions."
    />
    <meta
      property="twitter:image"
      content="https://flowhearts.com/images/homeimg.jpg"
    />

    {/* Structured Data */}
    <script type="application/ld+json">
      {`
        {
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": "FlowHearts Period Tracker",
          "url": "https://flowhearts.com/period-tracker",
          "description": "Track your menstrual cycle, ovulation, and fertile days with FlowHearts' Period Tracker for better women's health and hormone balance.",
          "applicationCategory": "HealthApplication",
          "operatingSystem": "Web",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
          },
          "creator": {
            "@type": "Organization",
            "name": "FlowHearts"
          }
        }
      `}
    </script>
  </Helmet>
      <div className="max-w-8xl mx-auto px-4 sm:px-6 py-8 sm:py-8">
        {/* Back to Home Button */}
        <div className="flex justify-start mb-4 sm:mb-6">
          <Link
            to="/"
            className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-md hover:bg-pink-50 transition-colors duration-200 text-sm sm:text-base"
          >
            <FiHome className="text-pink-500" />
            <span className="text-purple-700 font-medium">Back to Home</span>
          </Link>
        </div>


        <form onSubmit={handleSubmit}>
          <div className="women-card bg-white bg-opacity-80 backdrop-blur-lg rounded-3xl p-4 sm:p-8 shadow-xl">
            <h2 className="text-4xl font-cormorant sm:text-3xl font-bold mb-6 sm:mb-8 text-center text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-purple-600">
              Period Tracker
            </h2>


            {/* Circular Progress Indicator and Inputs */}
            <div className="flex flex-col lg:flex-row items-center justify-between gap-6 sm:gap-12">
              {/* Responsive Progress Circle */}
              <div className="relative w-48 h-48 sm:w-64 sm:h-64 lg:w-72 lg:h-72 mx-auto lg:mx-0 mb-6 lg:mb-0">
                <div className="absolute inset-0 rounded-full flex items-center justify-center shadow-lg bg-gradient-to-br from-pink-200 to-purple-200">
                  <div className="absolute inset-3 sm:inset-4 rounded-full flex items-center justify-center bg-gradient-to-br from-pink-100 to-purple-100">
                    <div className="text-center">
                      <span className="block text-3xl sm:text-4xl lg:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-purple-600">
                        {getCycleDay()}
                      </span>
                      <span className="text-sm sm:text-lg text-purple-500">
                        Day of Cycle
                      </span>
                    </div>
                  </div>
                </div>
                <svg className="absolute inset-0 w-full h-full transform -rotate-90">
                  <circle
                    cx="50%"
                    cy="50%"
                    r="45%"
                    fill="transparent"
                    stroke="#d8b4fe"
                    strokeWidth="8%"
                    strokeDasharray="100"
                    strokeDashoffset="0"
                  />
                  <circle
                    cx="50%"
                    cy="50%"
                    r="45%"
                    fill="transparent"
                    stroke="#ec4899"
                    strokeWidth="8%"
                    strokeDasharray="100"
                    strokeDashoffset={100 - getCycleProgress()}
                  />
                </svg>
              </div>


              <div className="flex-1 women-card bg-white bg-opacity-70 rounded-2xl p-4 sm:p-6 shadow-md w-full">
                <div className="mb-4 sm:mb-6">
                  <label className="block text-base sm:text-lg font-medium mb-1 sm:mb-2 text-purple-700">
                    <FiCalendar className="inline mr-2" />
                    Last Period Start Date
                  </label>
                  <input
                    type="date"
                    value={lastPeriodDate}
                    onChange={(e) => setLastPeriodDate(e.target.value)}
                    className="women-input w-full p-2 sm:p-3 rounded-xl border-2 border-purple-200 focus:border-pink-400 focus:ring-2 focus:ring-pink-200 text-sm sm:text-base"
                    required
                  />
                </div>


                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <label className="block text-base sm:text-lg font-medium mb-1 sm:mb-2 text-purple-700">
                      <FiClock className="inline mr-2" />
                      Cycle Length (days)
                    </label>
                    <input
                      type="number"
                      min="21"
                      max="45"
                      value={cycleLength}
                      onChange={(e) => setCycleLength(parseInt(e.target.value))}
                      className="women-input w-full p-2 sm:p-3 rounded-xl border-2 border-purple-200 focus:border-pink-400 focus:ring-2 focus:ring-pink-200 text-sm sm:text-base"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-base sm:text-lg font-medium mb-1 sm:mb-2 text-purple-700">
                      <FiClock className="inline mr-2" />
                      Period Length (days)
                    </label>
                    <input
                      type="number"
                      min="2"
                      max="10"
                      value={periodLength}
                      onChange={(e) =>
                        setPeriodLength(parseInt(e.target.value))
                      }
                      className="women-input w-full p-2 sm:p-3 rounded-xl border-2 border-purple-200 focus:border-pink-400 focus:ring-2 focus:ring-pink-200 text-sm sm:text-base"
                      required
                    />
                  </div>
                </div>


                <button
                  type="submit"
                  className="mt-4 sm:mt-6 w-full py-2 sm:py-3 px-4 bg-gradient-to-r from-pink-500 to-purple-600 text-white font-semibold rounded-xl shadow-md hover:from-pink-600 hover:to-purple-700 transition-all duration-300 text-sm sm:text-base"
                >
                  Calculate My Cycle
                </button>
              </div>
            </div>


            {/* Result Section - Only shown after submission */}
            {showResults && (
              <>
                <div className="mt-8 sm:mt-12 women-card bg-gradient-to-r from-pink-500 to-purple-600 rounded-2xl p-4 sm:p-6 shadow-lg text-white">
                  <h3 className="text-xl sm:text-2xl font-semibold mb-4 sm:mb-6 text-center">
                    Your Cycle Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                    <div className="p-3 sm:p-5 rounded-xl bg-white bg-opacity-20 backdrop-blur-sm">
                      <p className="text-xs sm:text-sm opacity-80">
                        Next Period
                      </p>
                      <p className="text-xl sm:text-2xl font-bold">
                        {formatDate(nextPeriod)}
                      </p>
                    </div>
                    <div className="p-3 sm:p-5 rounded-xl bg-white bg-opacity-30 backdrop-blur-sm">
                      <p className="text-xs sm:text-sm opacity-80">
                        Fertile Window
                      </p>
                      <p className="text-xl sm:text-2xl font-bold">
                        {fertileWindow.length > 0
                          ? `${formatDate(fertileWindow[0])} - ${formatDate(
                              fertileWindow[fertileWindow.length - 1]
                            )}`
                          : "--"}
                      </p>
                    </div>
                  </div>
                </div>


                {/* Calendar Overview */}
                <div className="mt-8 sm:mt-12">
                  <h3 className="text-lg sm:text-xl font-semibold text-center mb-3 sm:mb-4">
                    Cycle Overview Calendar
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
                    {generateMonthCalendar(prevMonth)}
                    {generateMonthCalendar(baseDate)}
                    {generateMonthCalendar(nextMonth)}
                  </div>
                  <div className="mt-3 sm:mt-4 flex flex-wrap items-center gap-3 sm:gap-6 justify-center text-xs sm:text-sm text-gray-700">
                    <div className="flex items-center gap-1 sm:gap-2">
                      <span className="w-3 h-3 sm:w-4 sm:h-4 bg-pink-400 rounded"></span>{" "}
                      Period Days
                    </div>
                    <div className="flex items-center gap-1 sm:gap-2">
                      <span className="w-3 h-3 sm:w-4 sm:h-4 bg-green-200 rounded"></span>{" "}
                      Fertile Window
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </form>
      </div>
      {/* SECTION 1: HOW TO USE (Simple Steps) */}
      <div className="max-w-8xl mx-auto px-4 sm:px-6 pb-4">
        <div className="women-card bg-white bg-opacity-90 rounded-3xl p-8 shadow-xl mb-12">
          <h2 className="text-3xl font-bold mb-8 text-center text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-purple-600">
            How to Use Your Period Tracker
          </h2>


          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Step 1 */}
            <div className="bg-pink-50 rounded-xl p-6 text-center border border-pink-200">
              <div className="w-12 h-12 bg-pink-500 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                <FiCalendar className="text-2xl" />
              </div>
              <h3 className="text-lg font-semibold mb-2 text-pink-700">
                Enter Your Last Period
              </h3>
              <p className="text-gray-700">
                Select the start date of your most recent menstrual cycle. This helps our period tracker app accurately map out your menstrual health patterns and provide personalized insights into your cycle.
              </p>
            </div>


            {/* Step 2 */}
            <div className="bg-purple-50 rounded-xl p-6 text-center border border-purple-200">
              <div className="w-12 h-12 bg-purple-500 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                <FiClock className="text-2xl" />
              </div>
              <h3 className="text-lg font-semibold mb-2 text-purple-700">
                Set Your Cycle Length
              </h3>
              <p className="text-gray-700">
                Input your average cycle duration (usually 21-35 days). Understanding your cycle length is key for effective ovulation prediction and maintaining hormone balance throughout the month.
              </p>
            </div>


            {/* Step 3 */}
            <div className="bg-pink-50 rounded-xl p-6 text-center border border-pink-200">
              <div className="w-12 h-12 bg-pink-500 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                <FiHome className="text-2xl" />
              </div>
              <h3 className="text-lg font-semibold mb-2 text-pink-700">
                Track & Predict
              </h3>
              <p className="text-gray-700">
                Get automatic predictions for future periods and fertile days. Stay ahead with fertility window alerts, helping you plan around your body's natural rhythms for better women's health management.
              </p>
            </div>
          </div>
        </div>


        {/* SECTION 2: HOW IT WORKS (Technical Explanation) */}
        <div className="women-card bg-white bg-opacity-90 rounded-3xl p-8 shadow-xl">
          <h2 className="text-3xl font-bold mb-8 text-center text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-500">
            The Science Behind Your Tracker
          </h2>


          <div className="space-y-8">
            {/* Calculation Method */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200">
              <h3 className="text-xl font-semibold mb-4 text-purple-800 flex items-center">
                <IoMdCalculator className="text-purple-600 mr-2 text-2xl" />
                Core Calculations
              </h3>


              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="bg-white p-4 rounded-lg">
                  <p className="font-semibold text-pink-600 mb-2 flex items-center">
                    <FiCalendar className="mr-1" /> Next Period Date
                  </p>
                  <code className="block bg-gray-50 p-2 rounded text-purple-700 font-mono">
                    Next Period = Last Start + Cycle Length
                  </code>
                  <p className="text-sm text-gray-600 mt-2">
                    Example: If last period started June 1 with a 28-day cycle →
                    June 29. This simple yet powerful formula forms the basis of menstrual cycle tracking, allowing for reliable period predictions.
                  </p>
                </div>


                <div className="bg-white p-4 rounded-lg">
                  <p className="font-semibold text-purple-600 mb-2 flex items-center">
                    <FiActivity className="mr-1" /> Fertile Window
                  </p>
                  <code className="block bg-gray-50 p-2 rounded text-purple-700 font-mono">
                    Ovulation Day = Next Period - 14
                    <br />
                    Fertile Days = Ovulation ± 3 days
                  </code>
                  <p className="text-sm text-gray-600 mt-2">
                    For June 29 next period → Ovulation June 15, fertile June
                    12-16. This calculation helps in identifying peak fertility periods, essential for those focusing on conception or natural family planning.
                  </p>
                </div>
              </div>
            </div>


            {/* Cycle Day Math */}
            <div className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-xl p-6 border border-pink-200">
              <h3 className="text-xl font-semibold mb-4 text-pink-800 flex items-center">
                <FiCalendar className="text-pink-600 mr-2 text-2xl" />
                Current Cycle Day
              </h3>
              <div className="bg-white p-4 rounded-lg mb-3">
                <code className="block bg-gray-50 p-2 rounded text-purple-700 font-mono">
                  Current Day = (Today - Last Start) + 1
                </code>
              </div>
              <p className="text-gray-700">
                If today is June 10 and last period started June 1:
                <br />
                <span className="font-mono bg-pink-100 px-2 py-1 rounded">
                  (10 - 1) + 1 = Day 10
                </span>{" "}
                of your cycle. Tracking cycle days like this can reveal patterns in energy levels and mood, supporting overall menstrual health awareness.
              </p>
            </div>


            {/* Biological Basis */}
            <div className="bg-white rounded-xl p-6 border border-purple-300">
              <h3 className="text-xl font-semibold mb-4 text-purple-800 flex items-center">
                <FiActivity className="text-purple-600 mr-2 text-2xl" />
                Biological Basis
              </h3>
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1 bg-pink-50 p-4 rounded-lg">
                  <p className="font-semibold text-pink-600 flex items-center">
                    <FiDroplet className="mr-1" /> Menstrual Phase
                  </p>
                  <p className="text-gray-700 text-sm">
                    Days 1-5: Shedding of uterine lining. This phase marks the beginning of a new cycle, often accompanied by cramps or fatigue, but it's a natural reset for your body.
                  </p>
                  <div className="h-1 bg-gradient-to-r from-pink-300 to-pink-100 rounded-full my-2"></div>
                </div>
                <div className="flex-1 bg-purple-50 p-4 rounded-lg">
                  <p className="font-semibold text-purple-600 flex items-center">
                    <FiTrendingUp className="mr-1" /> Follicular Phase
                  </p>
                  <p className="text-gray-700 text-sm">
                    Days 6-13: Egg maturation. Estrogen levels rise, boosting energy and skin glow – a great time for productivity and self-care routines.
                  </p>
                  <div className="h-1 bg-gradient-to-r from-purple-300 to-purple-100 rounded-full my-2"></div>
                </div>
                <div className="flex-1 bg-green-50 p-4 rounded-lg">
                  <p className="font-semibold text-green-600 flex items-center">
                    <FiBell className="mr-1" /> Ovulation
                  </p>
                  <p className="text-gray-700 text-sm">Day 14: Egg release. You might notice increased libido or mild twinges; it's your body's prime time for fertility.</p>
                  <div className="h-1 bg-gradient-to-r from-green-300 to-green-100 rounded-full my-2"></div>
                </div>
                <div className="flex-1 bg-yellow-50 p-4 rounded-lg">
                  <p className="font-semibold text-yellow-600 flex items-center">
                    <FiSun className="mr-1" /> Luteal Phase
                  </p>
                  <p className="text-gray-700 text-sm">
                    Days 15-28: Uterine preparation. Progesterone dominates, potentially causing PMS symptoms, but it's also ideal for reflection and gentle exercises.
                  </p>
                  <div className="h-1 bg-gradient-to-r from-yellow-300 to-yellow-100 rounded-full my-2"></div>
                </div>
              </div>
            </div>


            {/* Accuracy Note */}
            <div className="bg-white rounded-xl p-6 shadow-inner border border-pink-300">
              <h3 className="text-lg font-semibold mb-3 text-pink-800 flex items-center">
                <FiAlertTriangle className="text-pink-500 mr-2" />
                Important Note on Accuracy
              </h3>
              <p className="text-gray-700">
                The tracker uses{" "}
                <span className="font-semibold">
                  standard menstrual cycle patterns
                </span>
                . Your actual cycle may vary due to:
              </p>
              <ul className="mt-2 space-y-1 text-gray-700 text-sm">
                <li className="flex items-start">
                  <FiAlertCircle className="text-pink-500 mr-2 mt-0.5" />
                  <span>Stress (can alter cycle by 2-7 days). Managing stress through mindfulness can help stabilize your menstrual health.</span>
                </li>
                <li className="flex items-start">
                  <FiAlertCircle className="text-pink-500 mr-2 mt-0.5" />
                  <span>Hormonal changes (±1-14 days variation). Factors like diet or exercise can influence hormone balance and cycle regularity.</span>
                </li>
                <li className="flex items-start">
                  <FiAlertCircle className="text-pink-500 mr-2 mt-0.5" />
                  <span>Health conditions (PCOS, thyroid issues, etc.). Consulting a healthcare provider ensures personalized ovulation prediction and cycle management.</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      {/* NEW SECTION: Benefits of Tracking */}
      <div className="max-w-8xl mx-auto px-4 sm:px-6 pb-8">
        <div className="women-card bg-white bg-opacity-90 rounded-3xl p-8 shadow-xl mb-12">
          <h2 className="text-3xl font-bold mb-8 text-center text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-purple-600">
            Benefits of Menstrual Cycle Tracking
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Benefit 1 */}
            <div className="bg-purple-50 rounded-xl p-6 text-center border border-purple-200">
              <div className="w-12 h-12 bg-purple-500 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                <FiActivity className="text-2xl" />
              </div>
              <h3 className="text-lg font-semibold mb-2 text-purple-700">
                Empower Your Health
              </h3>
              <p className="text-gray-700">
                Regular tracking reveals patterns in your menstrual health, helping you spot irregularities early and make informed decisions about your well-being.
              </p>
            </div>
            {/* Benefit 2 */}
            <div className="bg-pink-50 rounded-xl p-6 text-center border border-pink-200">
              <div className="w-12 h-12 bg-pink-500 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                <FiTrendingUp className="text-2xl" />
              </div>
              <h3 className="text-lg font-semibold mb-2 text-pink-700">
                Plan with Confidence
              </h3>
              <p className="text-gray-700">
                Accurate fertility window insights allow for better planning, whether you're trying to conceive or simply syncing your life with your body's natural cycles.
              </p>
            </div>
            {/* Benefit 3 */}
            <div className="bg-purple-50 rounded-xl p-6 text-center border border-purple-200">
              <div className="w-12 h-12 bg-purple-500 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                <FiSun className="text-2xl" />
              </div>
              <h3 className="text-lg font-semibold mb-2 text-purple-700">
                Boost Daily Wellness
              </h3>
              <p className="text-gray-700">
                Understanding hormone balance throughout your cycle can guide nutrition, exercise, and rest, leading to improved mood and energy levels.
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* NEW SECTION: Tracking Tips */}
      <div className="max-w-8xl mx-auto px-4 sm:px-6 pb-8">
        <div className="women-card bg-white bg-opacity-90 rounded-3xl p-8 shadow-xl">
          <h2 className="text-3xl font-bold mb-8 text-center text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-500">
            Essential Tips for Effective Tracking
          </h2>
          <div className="space-y-8">
            <div className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-xl p-6 border border-pink-200">
              <h3 className="text-xl font-semibold mb-4 text-pink-800 flex items-center">
                <FiAlertCircle className="text-pink-600 mr-2 text-2xl" />
                Log Symptoms Daily
              </h3>
              <p className="text-gray-700">
                Note mood changes, energy levels, and physical symptoms. This enhances the accuracy of your period tracker app and provides deeper insights into your unique cycle.
              </p>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200">
              <h3 className="text-xl font-semibold mb-4 text-purple-800 flex items-center">
                <FiBell className="text-purple-600 mr-2 text-2xl" />
                Stay Consistent
              </h3>
              <p className="text-gray-700">
                Update your tracker regularly for better ovulation prediction. Over time, patterns emerge that can help in managing hormone balance and overall women's health.
              </p>
            </div>
            <div className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-xl p-6 border border-pink-200">
              <h3 className="text-xl font-semibold mb-4 text-pink-800 flex items-center">
                <FiTrendingUp className="text-pink-600 mr-2 text-2xl" />
                Combine with Lifestyle Factors
              </h3>
              <p className="text-gray-700">
                Track how sleep, diet, and exercise affect your cycle. This holistic approach turns simple menstrual cycle tracking into a powerful tool for personal wellness optimization.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


export default PeriodTracker;